import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable } from 'rxjs';
import {
  PaymentRequestDto,
  PaymentRequestStatus,
} from '../model/payment-request/payment-request.dto';
import { HttpClient } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../environments/environment';
import { QueryParamsService } from '../service/query-params/query-params.service';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { RegisterPaymentRequestInfo } from '../ngxs/payment-request/payment-request.actions';
import { IframeService } from '@app/service/iframe/iframe.service';
import { LoaderMessagingService } from '@app/service/loader-messaging/loader-messaging.service';

/**
 * Ensures that a user is unable to display the success page
 * for a non-settled payment request.
 */
@Injectable()
export class SuccessGuard implements CanActivate {
  constructor(
    private queryParamsService: QueryParamsService,
    private http: HttpClient,
    private store: Store,
    private iframeService: IframeService,
    private loaderMessagingService: LoaderMessagingService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    let id = this.queryParamsService.getQueryParams().id;

    if (!id) {
      id = state.root.queryParams.id;
    }

    return this.http
      .get<PaymentRequestDto>(`${SERVER_BASE_URL}/request/public/${id}`)
      .pipe(
        tap((response) =>
          this.store.dispatch(
            new RegisterPaymentRequestInfo({
              paymentRequest: response,
            })
          )
        )
      )
      .pipe(map((response) => this.handleResponse(response)));
  }

  private handleResponse(response: PaymentRequestDto): boolean {
    if (
      response.successUrl &&
      response.status === PaymentRequestStatus.SETTLED &&
      !this.redirectOrdered(response)
    ) {
      this.queryParamsService.setQueryParam('redirectOrdered', 'true');
      try {
        localStorage.setItem(response.id + 'successRedirect', 'true');
      } catch (error) {}
      if (this.iframeService.isThisAChildWindow()) {
        setTimeout(() => {
          this.iframeService.brodcastSuccess(response.successUrl);
        }, response.successUrlDelayInMs ?? 0);
      } else {
        if (this.iframeService.isThisInIframe()) {
          this.loaderMessagingService.successRedirect(response.successUrl);
        }
      }
    }
    return response.status === PaymentRequestStatus.SETTLED;
  }

  private redirectOrdered(response: PaymentRequestDto): boolean {
    return (
      this.queryParamsService.getQueryParams().redirectOrdered === 'true' ||
      localStorage.getItem(response.id + 'successRedirect') === 'true'
    );
  }
}
